import { memo, ReactNode } from 'react';
import { Container, Header, SpaceBetween } from '@amzn/awsui-components-core';
import { Epic, Task } from '@amzn/awscat-pattern-library-service-model';
import { getText, TiptapOutputType } from '@amzn/awscat-editor';

import SectionEditorControl from '../v2-common/SectionEditorControl';
import { SectionScrollTarget } from '../v2-common/SectionScrollTarget';
import { PatternCellField } from '../v2-content-editor/PatternCellField';
import { PatternCellSkillEditor } from '../v2-content-editor/PatternCellSkillEditor';
import { useScrollToCurrentPatternSection } from '../v2-hooks/useScrollToCurrentPatternSection';
import { SpecialSectionId } from '../../services/SectionOperations';
import {
  usePatternReadonlyEpicsViewModel,
  usePatternReadonlyTroubleshootingViewModel,
  usePatternReadonlyViewModel,
} from './PatternReadonlyViewModel';
import { useReportPageLoadTime } from '../../monitoring/PageLoadTimeReporter';
import styles from './PatternReadonlyView.module.scss';

// A troubleshooting issue/solution is typed `unknown` upstream but is always
// a Tiptap value at runtime — either serialized JSON (object) or plain text.
function isTiptapValue(value: unknown): value is TiptapOutputType {
  return typeof value === 'string' || (typeof value === 'object' && value !== null);
}

interface PatternReadonlyTaskRowProps {
  readonly task: Task;
  readonly contentId: string;
}

/** One read-only story row: title, description, and skills. */
function PatternReadonlyTaskRowView({ task, contentId }: PatternReadonlyTaskRowProps) {
  return (
    <tr>
      <td className={styles.cell}>
        <PatternCellField
          initialValue={task.title}
          isReadonly
          contentId={contentId}
          inlineTextFilter={{ type: 'epic_task_title', taskId: task.id }}
        />
      </td>
      <td className={styles.cell}>
        <PatternCellField
          initialValue={task.description}
          isReadonly
          contentId={contentId}
          inlineTextFilter={{ type: 'epic_task_description', taskId: task.id }}
        />
      </td>
      <td className={styles.cell}>
        <PatternCellSkillEditor skills={task.skills} isReadonly />
      </td>
    </tr>
  );
}

interface PatternReadonlyEpicProps {
  readonly epic: Epic;
  readonly contentId: string;
}

/** One read-only epic: title, optional description, and its stories table. */
function PatternReadonlyEpicView({ epic, contentId }: PatternReadonlyEpicProps) {
  const tasks = epic.tasks ?? [];
  const hasDescription = getText(epic.description).trim().length > 0;

  return (
    <Container
      disableContentPaddings
      header={
        <Header variant="h3">
          <div className={styles.epicTitle}>
            <PatternCellField
              initialValue={epic.title}
              isReadonly
              contentId={contentId}
              inlineTextFilter={{ type: 'epic_title', epicId: epic.id }}
            />
          </div>
        </Header>
      }
    >
      <SpaceBetween size="s">
        {hasDescription && (
          <div className={styles.descriptionArea}>
            <PatternCellField
              initialValue={epic.description}
              isReadonly
              contentId={contentId}
              inlineTextFilter={{ type: 'epic_description', epicId: epic.id }}
            />
          </div>
        )}
        {tasks.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.headerCell}>Story</th>
                <th className={styles.headerCell}>Description</th>
                <th className={styles.headerCell}>Skills</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <PatternReadonlyTaskRowView key={task.id} task={task} contentId={contentId} />
              ))}
            </tbody>
          </table>
        )}
      </SpaceBetween>
    </Container>
  );
}

interface PatternReadonlySectionProps {
  readonly title: string;
  readonly contentId: string;
}

/** Read-only Epics section: the section heading and each epic. */
function PatternReadonlyEpicsSectionView({ title, contentId }: PatternReadonlySectionProps) {
  const { epics } = usePatternReadonlyEpicsViewModel();

  if (epics.length == 0) {
    return <></>;
  }

  return (
    <Container header={<Header variant="h3">{title}</Header>}>
      <SpaceBetween size="m">
        {epics.map((epic) => (
          <PatternReadonlyEpicView key={epic.id} epic={epic} contentId={contentId} />
        ))}
      </SpaceBetween>
    </Container>
  );
}

/** Read-only Troubleshooting section: the section heading and an issue/solution table. */
function PatternReadonlyTroubleshootingSectionView({
  title,
  contentId,
}: PatternReadonlySectionProps) {
  const { rows } = usePatternReadonlyTroubleshootingViewModel();
  if (rows.length == 0) {
    return <></>;
  }

  return (
    <Container header={<Header variant="h3">{title}</Header>} disableContentPaddings>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.headerCell}>Issue</th>
            <th className={styles.headerCell}>Solution</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className={styles.cell}>
                {isTiptapValue(row.issue) && (
                  <PatternCellField
                    initialValue={row.issue}
                    isReadonly
                    contentId={contentId}
                    inlineTextFilter={{ type: 'troubleshooting_issue', troubleshootingId: row.id }}
                  />
                )}
              </td>
              <td className={styles.cell}>
                {isTiptapValue(row.solution) && (
                  <PatternCellField
                    initialValue={row.solution}
                    isReadonly
                    contentId={contentId}
                    inlineTextFilter={{
                      type: 'troubleshooting_solution',
                      troubleshootingId: row.id,
                    }}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Container>
  );
}

interface PatternReadonlyViewProps {
  /** Whether this body reports the view page's load time once it renders. */
  readonly isPageLoadTracked: boolean;
}

/** Read-only Pattern body: renders each section, dispatching epics and troubleshooting to their structured renderers. */
export const PatternReadonlyView = memo(function PatternReadonlyView({
  isPageLoadTracked,
}: PatternReadonlyViewProps) {
  const vm = usePatternReadonlyViewModel();
  useScrollToCurrentPatternSection(vm?.sections ?? []);

  // When the tracked Pattern body first renders (a view model exists, so this view returns more
  // than null), then report the view page as loaded.
  useReportPageLoadTime('view', isPageLoadTracked && vm !== undefined);

  if (!vm) return null;

  return (
    <SpaceBetween size="l">
      {vm.sections.map((section) => {
        let body: ReactNode;
        if (section.id === SpecialSectionId.Epics) {
          body = <PatternReadonlyEpicsSectionView title={section.title} contentId={vm.contentId} />;
        } else if (section.id === SpecialSectionId.Troubleshooting) {
          body = (
            <PatternReadonlyTroubleshootingSectionView
              title={section.title}
              contentId={vm.contentId}
            />
          );
        } else {
          body = (
            <SectionEditorControl
              node={section}
              contentId={vm.contentId}
              title={section.title}
              isReadonly
            />
          );
        }

        return (
          <SectionScrollTarget key={section.id} node={section}>
            {body}
          </SectionScrollTarget>
        );
      })}
    </SpaceBetween>
  );
});
